import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";

import api, { setAccessToken, clearAccessToken } from "../api";

export const AuthContext = createContext(null);

function parseJwt(token) {
  try {
    const [, payload] = token.split(".");
    const norm = payload.replace(/-/g, "+").replace(/_/g, "/");
    const pad = "===".slice((norm.length + 3) % 4);
    return JSON.parse(atob(norm + pad));
  } catch {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(null);
  const [role, setRole] = useState(null); // manager | seller
  const [authReady, setAuthReady] = useState(false);

  const isAuthenticated = !!token;

  // ------------------------------------------------
  // LOGIN
  // ------------------------------------------------
  const login = useCallback(async (type, login, password) => {
    const { data } = await api.post(
      `/api/${type}/auth/login`,
      { login, password },
      { _skipAuthRetry: true }
    );

    const jwt = data.access_token;

    setAccessToken(jwt);
    setToken(jwt);

    const payload = parseJwt(jwt);
    setRole(type);

    localStorage.setItem("auth_token", jwt);
    localStorage.setItem("auth_role", type);

    return true;
  }, []);

  // ------------------------------------------------
  // LOGOUT
  // ------------------------------------------------
  const logout = useCallback(async () => {
    try {
      if (role) {
        await api.post(`/api/${role}/auth/logout`, {}, { _skipAuthRetry: true });
      }
    } catch {}

    clearAccessToken();
    setToken(null);
    setRole(null);

    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_role");
  }, [role]);

  // ------------------------------------------------
  // INITIAL LOAD (auto restore)
  // ------------------------------------------------
  useEffect(() => {
    (async () => {
      try {
        const storedToken = localStorage.getItem("auth_token");
        const storedRole = localStorage.getItem("auth_role");

        if (!storedToken || !storedRole)
          throw new Error("no session");

        setAccessToken(storedToken);
        setToken(storedToken);
        setRole(storedRole);

        // пробуем тихий refresh
        const { data } = await api.post(
          `/api/${storedRole}/auth/refresh`,
          null,
          { _skipAuthRetry: true }
        );

        setAccessToken(data.access_token);
        setToken(data.access_token);

        localStorage.setItem("auth_token", data.access_token);
      } catch {
        clearAccessToken();
        setToken(null);
        setRole(null);
        localStorage.removeItem("auth_token");
        localStorage.removeItem("auth_role");
      } finally {
        setAuthReady(true);
      }
    })();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        role,
        isAuthenticated,
        authReady,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
